import { ArrowLeftOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, Row, Select, Typography, message } from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const { Text } = Typography;
const { Option } = Select;

const orderStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Dispatched", value: "dispatched" },
  { label: "In transit", value: "in transit" },
  { label: "Delivered", value: "delivered" },
  { label: "Rejected", value: "rejected" },
];

const EditOrder = () => {
  const { id: orderId } = useParams();
  const navigate = useNavigate();

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  const [products, setProducts] = useState([{ productId: null, quantity: 1, category: null }]);
  const [productOptions, setProductOptions] = useState([]);
  const [orderStatus, setOrderStatus] = useState(null);
  const [requestFrom, setRequestFrom] = useState(null);
  const [requestFromOptions, setRequestFromOptions] = useState([]);
  const [address, setAddress] = useState(null);
  const [addressOptions, setAddressOptions] = useState([]);

  useEffect(() => {
    // Load dropdown options
    axios.get(`${process.env.REACT_APP_API_BASEURL}/product`).then((res) => {
      const options = res.data.map((item) => ({
        label: item.productName,
        value: item._id,
        category: item.productCategory,
      }));
      setProductOptions(options);
    });

    axios.get(`${process.env.REACT_APP_API_BASEURL}/orders`).then((res) => {
      const employees = res.data
        .map((order) => order.requestFrom)
        .filter(Boolean)
        .reduce((acc, curr) => {
          if (!acc.find((item) => item._id === curr._id)) {
            acc.push(curr);
          }
          return acc;
        }, []);
      const options = employees.map((emp) => ({
        label: emp.employeeName,
        value: emp._id,
      }));
      setRequestFromOptions(options);
    });

    axios.get(`${process.env.REACT_APP_API_BASEURL}/address`).then((res) => {
      const options = res.data.map((item) => ({
        label: `${item.addressDetails}, ${item.city}`,
        value: item._id,
      }));
      setAddressOptions(options);
    });

    // Load order data
    axios
      .get(`${process.env.REACT_APP_API_BASEURL}/orders/${orderId}`)
      .then((res) => {
        const data = res.data;

        setProducts(
          data.product.map((p) => ({
            productId:
              typeof p.product_id === "object"
                ? p.product_id._id
                : p.product_id,
            quantity: p.quantity,
            category: p.orderType,
          }))
        );

        setOrderStatus(data.orderStatus);
        setRequestFrom(data.requestFrom?._id);
        setAddress(data.address?._id);
      })
      .catch(() => {
        message.error("Failed to load order data");
      });
  }, [orderId]);

  const handleProductChange = (index, key, value) => {
    const updated = [...products];
    updated[index][key] = value;
    setProducts(updated);
  };

  const handleAddProduct = () => {
    setProducts([...products, { productId: null, quantity: 1, category: null }]);
  };

  const handleSubmit = () => {
    if (
      !orderStatus ||
      !requestFrom ||
      !address ||
      products.some((p) => !p.productId || !p.category || !p.quantity)
    ) {
      message.error("Please fill all required fields");
      return;
    }

    const orderPayload = {
      product: products.map((p) => {
        const selectedProduct = productOptions.find(
          (prod) => prod.value === p.productId
        );

        return {
          product_id: p.productId,
          productName: selectedProduct?.label || "",
          quantity: p.quantity,
          orderType: p.category,
        };
      }),
      orderStatus,
      requestFrom,
      address,
    };

    axios
      .put(`${process.env.REACT_APP_API_BASEURL}/orders/${orderId}`, orderPayload)
      .then(() => {
        message.success("Order updated successfully!");
        navigate(-1);
      })
      .catch(() => {
        message.error("Failed to update order");
      });
  };

  return (
    <Card title="Edit Order">
      {products.map((product, index) => (
        <Row gutter={16} key={index} style={{ marginBottom: 12 }}>
          <Col span={8}>
            <Text strong>Product Name</Text>
            <Select
              showSearch
              placeholder="Select Product"
              value={product.productId}
              onChange={(value) => handleProductChange(index, "productId", value)}
              style={{ width: "100%" }}
            >
              {productOptions.map((prod) => (
                <Option key={prod.value} value={prod.value}>
                  {prod.label}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={8}>
            <Text strong>Quantity</Text>
            <Input
              type="number"
              value={product.quantity}
              onChange={(e) =>
                handleProductChange(index, "quantity", parseInt(e.target.value))
              }
              placeholder="Enter Quantity"
            />
          </Col>

          <Col span={8}>
            <Text strong>Product Category</Text>
            <Select
              showSearch
              placeholder="Select Category"
              value={product.category}
              onChange={(value) => handleProductChange(index, "category", value)}
              style={{ width: "100%" }}
            >
              <Option value="POSM">POSM</Option>
              <Option value="Assets">Assets</Option>
            </Select>
          </Col>
        </Row>
      ))}

      <Row>
        <Col span={24}>
          <Button type="default" icon={<PlusOutlined />} onClick={handleAddProduct} style={{ width: 120, marginBottom: 24, borderColor: PRIMARY_RED, color: PRIMARY_RED }}>
            Add Product
          </Button>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Text strong>Order Status</Text>
          <Select
            showSearch
            placeholder="Select Order Status"
            value={orderStatus}
            onChange={setOrderStatus}
            style={{ width: 300 }}
          >
            {orderStatusOptions.map((status) => (
              <Option key={status.value} value={status.value}>
                {status.label}
              </Option>
            ))}
          </Select>
        </Col>

        <Col span={12}>
          <Text strong>Request From</Text>
          <Select
            showSearch
            placeholder="Select Employee"
            value={requestFrom}
            onChange={setRequestFrom}
            style={{ width: 300 }}
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toString()
                .toLowerCase()
                .includes(input.trim().toLowerCase())
            }
          >
            {requestFromOptions.map((emp) => (
              <Option key={emp.value} value={emp.value}>
                {emp.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row style={{ marginBottom: 24 }}>
        <Col span={24}>
          <Text strong>Address</Text>
          <Row>
            <Select
              showSearch
              placeholder="Select Address"
              value={address}
              onChange={setAddress}
              style={{ width: 300 }}
            >
              {addressOptions.map((addr) => (
                <Option key={addr.value} value={addr.value}>
                  {addr.label}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col>
          <Button type="primary" icon={<EditOutlined />} style={{ backgroundColor: PRIMARY_RED }} onClick={handleSubmit}>
            Edit Order
          </Button>
        </Col>
        <Col>
          <Button type="link" style={{ color: PRIMARY_RED }} icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
        </Col>
      </Row>
    </Card>
  );
};

export default EditOrder;
